import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Checkbox, notification } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import Input from 'components/Atom/Input';
import { Button } from 'components/Atom/Button';
import { AccountID } from 'assets/icons';
import Cookies from 'js-cookie';
import { getCubejsToken } from 'utils/cubejs';
import { setAccessToken, setRefreshToken } from 'utils/storage';
import storage from 'utils/storage';
import { authSdk } from 'lib/auth';
import { isWebAuthnSupported, getPasskeyEmail, setPasskeyEmail, clearPasskeyEmail } from 'postex-auth-sdk-stage';
import './assets/style.css';

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const toast = (args) => {
  const fn = args.variant === 'destructive' ? notification.error : notification.success;
  fn({ message: args.title, description: args.description, placement: 'bottomRight' });
};

const defaultRp = () => ({
  name: 'XPay Back Office',
  host: typeof window !== 'undefined' ? window.location.hostname : 'localhost',
});

const realmOpts = (realm) => {
  const rk = typeof realm === 'string' && realm.trim() ? realm.trim() : '';
  return rk ? { realm: rk } : undefined;
};

const LoginPageShell = ({ heading, subtext, children }) => {
  const currentYear = new Date().getFullYear();
  return (
    <div className='login-wrapper'>
      <div className='login-form login-bg-wrapper'>
        <div className='form-wrapper'>
          <img className='logo' alt='Xpay logo' src='/XPay.svg' />
          <div className='welcome-text'>
            <p className='heading'>{heading}</p>
            <div className='subtext'>{subtext}</div>
          </div>
          {children}
        </div>

        <div className='form-footer-note pb-6'>
          <div className='copyright'>
            <a href='https://xpay.postexglobal.com' target='_blank' rel='noopener noreferrer' className='pr-2 hover:underline' style={{ color: '#4f4d55' }}>
              ©{currentYear} PostEx.
            </a>{' '}
          </div>
          <p className='terms'>
            <a href='https://xpay.postexglobal.com' target='_blank' rel='noopener noreferrer' className='pr-2 hover:underline'>
              Terms of Service
            </a>
            |
            <a href='https://xpay.postexglobal.com' target='_blank' rel='noopener noreferrer' className='pl-2 hover:underline'>
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
      <div>
        <img src='/loginImage.svg' alt='image' />
      </div>
    </div>
  );
};

const PostExAuthLogin = () => {
  const [form] = Form.useForm();
  const [rememberMe, setRememberMe] = useState(true);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [redirectNow, setRedirectNow] = useState(false);
  const [webauthnReady, setWebauthnReady] = useState(false);

  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [resendOtpLoading, setResendOtpLoading] = useState(false);
  const [showAddPasskeyPrompt, setShowAddPasskeyPrompt] = useState(false);
  const [otpResponseForPrompt, setOtpResponseForPrompt] = useState(null);
  const [authChallenge, setAuthChallenge] = useState(undefined);
  const [authCredentialIds, setAuthCredentialIds] = useState(undefined);
  const [authRp, setAuthRp] = useState(undefined);
  const debounceRef = useRef(null);
  const otpInputRefs = useRef([]);
  const prefilledStatusLookupRef = useRef(null);
  const activeRealmRef = useRef('');
  const [debounceIds, setDebounceIds] = useState({ email: '', realm: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const storedEmail = await getPasskeyEmail();
      const cookieRealm = Cookies.get('accountId') || '';
      if (storedEmail) {
        const normalizedEmail = storedEmail.trim();
        const normalizedRealm = cookieRealm.trim();
        form.setFieldsValue({ email: storedEmail, account_id: cookieRealm });
        setDebounceIds({ email: normalizedEmail, realm: normalizedRealm });
        setEmail(normalizedEmail);
        prefilledStatusLookupRef.current = { email: normalizedEmail, realm: normalizedRealm };
        setStatusLoading(true);
        try {
          const status = await authSdk.getStatus(storedEmail, realmOpts(cookieRealm));
          if (status.status === 'webauthn_ready' && status.challenge && status.credentialIds?.length) {
            setWebauthnReady(true);
            setAuthChallenge(status.challenge);
            setAuthCredentialIds(status.credentialIds);
            if (status.rp) setAuthRp(status.rp);
          } else {
            setWebauthnReady(false);
            if (status.status !== 'session_found' && status.status !== 'no_session') {
              await clearPasskeyEmail();
            }
          }
        } catch {
          await clearPasskeyEmail();
          setWebauthnReady(false);
        } finally {
          setStatusLoading(false);
        }
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const em = debounceIds.email;
    const rk = debounceIds.realm;
    if (!em || !validateEmail(em)) {
      setWebauthnReady(false);
      setAuthChallenge(undefined);
      setAuthCredentialIds(undefined);
      setAuthRp(undefined);
      setStatusLoading(false);
      return;
    }
    if (prefilledStatusLookupRef.current?.email === em && prefilledStatusLookupRef.current?.realm === rk) {
      prefilledStatusLookupRef.current = null;
      return;
    }
    setStatusLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const status = await authSdk.getStatus(em, realmOpts(rk));
        if (status.status === 'webauthn_ready' && status.challenge && status.credentialIds?.length) {
          setWebauthnReady(true);
          setAuthChallenge(status.challenge);
          setAuthCredentialIds(status.credentialIds);
          if (status.rp) setAuthRp(status.rp);
        } else {
          setWebauthnReady(false);
          setAuthChallenge(undefined);
          setAuthCredentialIds(undefined);
          setAuthRp(undefined);
        }
      } catch {
        setWebauthnReady(false);
        setAuthChallenge(undefined);
        setAuthCredentialIds(undefined);
        setAuthRp(undefined);
      } finally {
        setStatusLoading(false);
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [debounceIds.email, debounceIds.realm]);

  useEffect(() => {
    if (redirectNow) {
      navigate('/', { replace: true, state: { pathname: '/auth/login', loginSuccess: true } });
    }
  }, [redirectNow, navigate]);

  useEffect(() => {
    if (showOtpInput) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 0);
    }
  }, [showOtpInput]);

  const onAuthSuccess = async (data) => {
    if (!data?.access_token) throw new Error('No token returned from login API');

    setAccessToken(data.access_token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    window.localStorage.setItem('name', JSON.stringify(data?.name ?? 'Admin'));
    window.localStorage.setItem('email', data?.email ?? '');
    if (data?.realm != null) window.localStorage.setItem('realm', data.realm);
    if (data?.apps != null) storage.setApps(data.apps);
    storage.setRemember(rememberMe);
    rememberMe && activeRealmRef.current ? Cookies.set('accountId', activeRealmRef.current, { expires: 99 }) : Cookies.remove('accountId');
    setRedirectNow(true);
    getCubejsToken();
  };

  const registerPasskeyForEmail = async () => {
    if (!isWebAuthnSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }
    await authSdk.registerPasskey(email);
    await setPasskeyEmail(email);
  };

  const authenticateWithPasskey = async (challenge, credentialIds, rp) => {
    if (!isWebAuthnSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    const effectiveRp = rp?.name && rp?.host ? rp : defaultRp();
    const authData = await authSdk.authenticateWithPasskey({
      challenge,
      rp: effectiveRp,
      credentialIds,
    });
    await setPasskeyEmail(email);
    await onAuthSuccess(authData);
  };

  const handleVerifyOTP = async () => {
    if (otpValue.length !== 6) {
      toast({
        title: 'Invalid OTP',
        description: 'Please enter a valid 6-digit code.',
        variant: 'destructive',
      });
      return;
    }

    setVerifyOtpLoading(true);
    try {
      const otpResponse = await authSdk.verifyOTP(otpValue);
      setOtpResponseForPrompt(otpResponse);
      setShowAddPasskeyPrompt(true);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Verification failed';
      toast({
        title: 'Verification failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendOtpLoading(true);
    setOtpValue('');
    try {
      await authSdk.resendOTP();
      toast({
        title: 'OTP Sent',
        description: 'A new verification code has been sent to your email.',
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to resend OTP';
      toast({
        title: 'Resend failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setResendOtpLoading(false);
    }
  };

  const handleBackFromOtp = () => {
    setOtpValue('');
    setShowOtpInput(false);
  };

  const handleOtpEnter = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    if (verifyOtpLoading || resendOtpLoading || otpValue.length < 6) return;
    handleVerifyOTP();
  };

  const handleOtpDigitChange = (index, nextValue) => {
    const digit = (nextValue || '').replace(/\D/g, '').slice(-1);
    const nextOtp = otpValue.split('');
    nextOtp[index] = digit;
    const mergedOtp = nextOtp.join('').slice(0, 6);
    setOtpValue(mergedOtp);

    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      if (otpValue[index]) {
        const nextOtp = otpValue.split('');
        nextOtp[index] = '';
        setOtpValue(nextOtp.join(''));
      } else if (index > 0) {
        otpInputRefs.current[index - 1]?.focus();
        const nextOtp = otpValue.split('');
        nextOtp[index - 1] = '';
        setOtpValue(nextOtp.join(''));
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      otpInputRefs.current[index - 1]?.focus();
      return;
    }

    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    event.preventDefault();
    setOtpValue(pasted);
    const focusIndex = Math.min(pasted.length - 1, 5);
    otpInputRefs.current[focusIndex]?.focus();
  };

  const handleSkipAddPasskey = async () => {
    if (otpResponseForPrompt) {
      await onAuthSuccess(otpResponseForPrompt);
      setOtpResponseForPrompt(null);
      setShowAddPasskeyPrompt(false);
    }
  };

  const handleAddPasskeyAfterOTP = async () => {
    if (!otpResponseForPrompt) return;
    setLoading(true);
    try {
      await registerPasskeyForEmail();
      await onAuthSuccess(otpResponseForPrompt);
      setOtpResponseForPrompt(null);
      setShowAddPasskeyPrompt(false);
    } catch (err) {
      if (err?.name === 'InvalidStateError') {
        toast({
          title: 'Passkey already exists',
          description: 'This device is already registered.',
        });
      } else if (err?.name === 'NotAllowedError') {
        toast({
          title: 'Passkey creation cancelled',
          description: 'You can create a passkey later from this device.',
        });
      } else {
        const msg = err?.response?.data?.message || err?.message || 'Passkey registration failed';
        toast({
          title: 'Passkey registration failed',
          description: msg,
          variant: 'destructive',
        });
      }
      await onAuthSuccess(otpResponseForPrompt);
      setOtpResponseForPrompt(null);
      setShowAddPasskeyPrompt(false);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueWithPasskey = async () => {
    if (!isWebAuthnSupported()) {
      toast({
        title: 'Passkeys not supported',
        description: "Your browser/device doesn't support passkeys.",
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const challenge = authChallenge;
      const credentialIds = authCredentialIds;
      const rp = authRp;

      if (!challenge || !credentialIds?.length) {
        throw new Error('No passkey data available');
      }

      await authenticateWithPasskey(challenge, credentialIds, rp);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '';
      toast({
        title: 'Passkey sign-in failed',
        description: msg || 'Unable to sign in with passkey.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async (realm, em) => {
    setLoading(true);
    try {
      const response = await authSdk.initiateAuth(em, realmOpts(realm));
      if (response.status === 'otp_sent') {
        setOtpValue('');
        setShowOtpInput(true);
        toast({
          title: 'OTP Sent',
          description: 'Please check your email for the verification code.',
        });
      } else if (response.status === 'webauthn_challenge') {
        if (response.challenge && response.credentialIds?.length) {
          setAuthChallenge(response.challenge);
          setAuthCredentialIds(response.credentialIds);
          if (response.rp) setAuthRp(response.rp);
          await authenticateWithPasskey(response.challenge, response.credentialIds, response.rp);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message;
      toast({
        title: 'Failed to start sign-in',
        description: msg || 'Unable to initiate verification.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const onPrimaryFinish = async (values) => {
    const realm = values?.account_id?.trim?.() || '';
    const em = values?.email?.trim?.().toLowerCase() || '';
    activeRealmRef.current = realm;
    setEmail(em);
    if (webauthnReady) {
      await handleContinueWithPasskey();
    } else {
      await handleContinue(realm, em);
    }
  };

  const handleClearEmail = async () => {
    await clearPasskeyEmail();
    setOtpValue('');
    setShowOtpInput(false);
    form.resetFields();
    setDebounceIds({ email: '', realm: '' });
    setEmail('');
    setWebauthnReady(false);
    setAuthChallenge(undefined);
    setAuthCredentialIds(undefined);
    setAuthRp(undefined);
  };

  if (showAddPasskeyPrompt) {
    return (
      <LoginPageShell heading='Add a Passkey?' subtext='Add a passkey to sign in faster next time using Face ID, Touch ID, or Windows Hello.'>
        <Form layout='vertical'>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'right', marginTop: '8px' }}>
              <Button block className='button-theme rounded-lg' onClick={handleAddPasskeyAfterOTP} loading={loading}>
                Add Passkey
              </Button>
            </div>
          </Form.Item>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
              <Button className='text-button' type='button' disabled={loading} onClick={handleSkipAddPasskey}>
                Skip for now
              </Button>
            </div>
          </Form.Item>
        </Form>
      </LoginPageShell>
    );
  }

  if (showOtpInput) {
    const otpActionLoading = verifyOtpLoading || resendOtpLoading;

    return (
      <LoginPageShell
        heading='Enter Verification Code'
        subtext={
          <>
            We sent a code to the email associated with your account: <strong>{email}</strong>
          </>
        }>
        <div onKeyDown={handleOtpEnter}>
          <Form layout='vertical'>
            <Form.Item label='Verification code' className='customInput mobileInputView'>
              <div className='otp-input-group' onPaste={handleOtpPaste}>
                {Array.from({ length: 6 }, (_, index) => (
                  <input
                    key={index}
                    ref={(element) => {
                      otpInputRefs.current[index] = element;
                    }}
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={1}
                    value={otpValue[index] || ''}
                    onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpDigitKeyDown(index, e)}
                    className='otp-digit-input'
                    aria-label={`OTP digit ${index + 1}`}
                  />
                ))}
              </div>
            </Form.Item>
            <Form.Item>
              <div style={{ display: 'flex', justifyContent: 'right', marginTop: '8px' }}>
                <Button block className='button-theme rounded-lg' onClick={handleVerifyOTP} loading={verifyOtpLoading} disabled={otpActionLoading || otpValue.length < 6}>
                  Verify
                </Button>
              </div>
            </Form.Item>
            <div className='form-footer' style={{ marginTop: '4px' }}>
              <Button className='text-button' type='button' disabled={otpActionLoading} onClick={handleResendOTP} loading={resendOtpLoading}>
                Resend OTP
              </Button>
              <Button className='text-button' type='button' disabled={otpActionLoading} onClick={handleBackFromOtp}>
                Back
              </Button>
            </div>
          </Form>
        </div>
      </LoginPageShell>
    );
  }

  const primaryLabel = webauthnReady ? 'Continue with Passkey' : 'Continue';

  return (
    <LoginPageShell heading='Hi, Welcome to Admin Portal' subtext='Empowering your retail success'>
      <Form
        form={form}
        layout='vertical'
        onFinish={onPrimaryFinish}
        initialValues={{ account_id: Cookies.get('accountId') || '' }}
        onValuesChange={(_, all) => {
          setDebounceIds({
            email: (all.email || '').trim(),
            realm: (all.account_id || '').trim(),
          });
        }}>
        <Form.Item name='account_id' label='Account ID' className='customInput mobileInputView' rules={[{ required: true, message: 'Account id is required' }]}>
          <Input placeholder='Enter Account ID' name='account_id' className='input-placeholder' suffix={<AccountID style={{ color: '#7F7D83' }} />} />
        </Form.Item>
        <Form.Item
          name='email'
          label='Email'
          className='customInput mobileInputView'
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Please enter a valid email address' },
          ]}>
          <Input placeholder='Enter Email' name='email' className='input-placeholder' suffix={<MailOutlined style={{ color: '#7F7D83' }} />} />
        </Form.Item>

        <div className='form-footer'>
          <Checkbox className='checkbox' checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} textClassName='checkbox-label'>
            <span style={{ fontWeight: '475', color: '#000000' }}>Remember me</span>
          </Checkbox>
          <Button className='text-button' type='button' onClick={() => navigate('/auth/forgot')}>
            Forgot Password?
          </Button>
        </div>

        <Form.Item>
          <div style={{ display: 'flex', justifyContent: 'right', marginTop: '8px' }}>
            <Button block className='button-theme rounded-lg' htmlType='submit' loading={loading || statusLoading}>
              {primaryLabel}
            </Button>
          </div>
        </Form.Item>

        {(debounceIds.email || email) && (
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button className='text-button' type='button' disabled={loading} onClick={handleClearEmail}>
                Use a different account
              </Button>
            </div>
          </Form.Item>
        )}

        <p className='terms' style={{ fontSize: 12, marginTop: 8, textAlign: 'center', color: '#5f5f5f' }}>
          {webauthnReady
            ? "You'll be prompted by your device to use a passkey (Face ID / Touch ID / Windows Hello)."
            : 'Enter your account details to sign in. We will email you a verification code when needed.'}
        </p>
      </Form>
    </LoginPageShell>
  );
};

export default PostExAuthLogin;
